
/**
 * Utility functions for handling dates as simple YYYY-MM-DD strings without timezone issues
 */

/**
 * Formats a Date object to YYYY-MM-DD string using local timezone
 * This avoids timezone issues that occur with date-fns format()
 * @param date - Date object to format
 * @returns Date string in YYYY-MM-DD format
 */
export const formatDateToString = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parses a date string (YYYY-MM-DD) into separate parts
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Object with year, month, day as numbers or undefined if invalid
 */
export const parseDateString = (dateString: string | null | undefined) => {
  if (!dateString) return { year: undefined, month: undefined, day: undefined };
  
  const [yearStr, monthStr, dayStr] = dateString.split('-');
  const year = parseInt(yearStr);
  const month = parseInt(monthStr);
  const day = parseInt(dayStr);
  
  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    return { year: undefined, month: undefined, day: undefined };
  }
  
  return { year, month, day };
};

/**
 * Formats a set of date parts into a YYYY-MM-DD string
 * @param year - Year (e.g. 2023)
 * @param month - Month (1-12)
 * @param day - Day (1-31)
 * @returns Date string in YYYY-MM-DD format or empty string if invalid
 */
export const formatDateParts = (year?: number, month?: number, day?: number): string => {
  if (!year || !month || !day) return '';
  
  const monthStr = month.toString().padStart(2, '0');
  const dayStr = day.toString().padStart(2, '0');
  
  return `${year}-${monthStr}-${dayStr}`;
};

/**
 * Formats a date for display in a user-friendly format
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns User-friendly date string
 */
export const formatDateForDisplay = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  
  const { year, month, day } = parseDateString(dateString);
  if (!year || !month || !day) return '';
  
  // Create a date string in the format "Month Day, Year"
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  return `${months[month - 1]} ${day}, ${year}`;
};
